import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { date } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { SuperEdit } from "@/components/SuperEdit";
import { SuperDelete } from "@/components/SuperDelete";
import { BalanceCard, LeaveForm, LeaveList, AddButton } from "@/components/hr/HrBits";
import ProfileForm from "@/components/hr/ProfileForm";
import CertsPanel from "@/components/hr/CertsPanel";
import ApprenticePanel from "@/components/hr/ApprenticePanel";

function Adjust({ uid, year, onDone }) {
  const [f, setF] = useState({ kind: "adjustment", days: "", reason: "" });
  const save = async () => {
    try { await api.post(`/hr/balance/${uid}/adjust`, { ...f, year }); toast.success("Balance updated"); setF({ ...f, days: "", reason: "" }); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="card-industrial p-4 mt-4 grid gap-2 sm:grid-cols-[170px_110px_1fr_auto]" data-testid="balance-adjust">
      <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger data-testid="adjust-kind"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="adjustment">Manual adjustment</SelectItem><SelectItem value="carry_over">Carry-over</SelectItem></SelectContent></Select>
      <Input type="number" step="0.5" placeholder="+/- days" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} data-testid="adjust-days" />
      <Input placeholder="Reason (required)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="adjust-reason" />
      <Button onClick={save} disabled={!f.days || !f.reason} data-testid="adjust-save">Apply</Button>
    </div>
  );
}

function LeaveTab({ uid }) {
  const [b, setB] = useState(null);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get(`/hr/balance/${uid}`).then((r) => setB(r.data));
    api.get("/hr/leave", { params: { user_id: uid } }).then((r) => setRows(r.data));
  }, [uid]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-4">
      <div className="flex justify-end mb-3"><LeaveForm userId={uid} onDone={load} trigger={<AddButton label="Add leave" testid="person-leave-add" />} /></div>
      <BalanceCard b={b} />
      {b && <Adjust uid={uid} year={b.year} onDone={load} />}
      {b?.history?.length > 0 && (
        <div className="card-industrial divide-y divide-border mt-3 text-xs" data-testid="adjust-history">
          {b.history.map((h) => <div key={h.id} className="px-4 py-2 flex justify-between"><span>{h.kind === "carry_over" ? "Carry-over" : "Adjustment"} · {h.reason} · {h.by}</span><span className="font-mono-num flex items-center gap-2">{h.days > 0 ? "+" : ""}{h.days}d · {date(h.created_at)}<SuperEdit entity="leave_adjustment" record={h} label="adjustment" onDone={load} /><SuperDelete entity="leave_adjustment" id={h.id} label="this adjustment" onDone={load} /></span></div>)}
        </div>
      )}
      <h3 className="font-heading font-bold uppercase text-sm mt-6 mb-2">Leave history</h3>
      <LeaveList rows={rows} onDeleted={load} />
    </div>
  );
}

export default function PersonDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const load = useCallback(() => { api.get(`/hr/people/${id}`).then((r) => setP(r.data)); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!p) return <div className="p-6 text-muted-foreground">Loading…</div>;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl">
      <Link to="/hr/people" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" />People</Link>
      <h1 className="font-heading text-3xl font-extrabold uppercase mb-1" data-testid="person-name">{p.name}</h1>
      <p className="text-sm text-muted-foreground mb-5">{p.profile.job_title || p.role} · {p.home_location} · {p.email}</p>
      <Tabs defaultValue="details">
        <TabsList><TabsTrigger value="details" data-testid="tab-person-details">Details</TabsTrigger><TabsTrigger value="leave" data-testid="tab-person-leave">Leave</TabsTrigger>
          <TabsTrigger value="certs" data-testid="tab-person-certs">Certs</TabsTrigger><TabsTrigger value="apprentice" data-testid="tab-person-apprentice">Apprentice</TabsTrigger></TabsList>
        <TabsContent value="details"><div className="mt-4"><ProfileForm person={p} hr onSaved={load} /></div></TabsContent>
        <TabsContent value="leave"><LeaveTab uid={id} /></TabsContent>
        <TabsContent value="certs"><div className="mt-4"><CertsPanel userId={id} hr /></div></TabsContent>
        <TabsContent value="apprentice"><div className="mt-4"><ApprenticePanel uid={id} hr /></div></TabsContent>
      </Tabs>
    </div>
  );
}
